import { AboveTheFoldOnlyServerRender } from 'above-the-fold-only-server-render';
import PropTypes from 'prop-types';
import React from 'react';
import Immutable from 'immutable';
import ImmutablePropTypes from 'react-immutable-proptypes';
import { TransitionGroup, CSSTransition } from 'react-transition-group';
import Checkbox from '@bibliocommons/deprecated-base-checkbox';
import { PrintButton } from '@bibliocommons/print';
import { bindAll } from '@bibliocommons/utils-react';
import { withRouter } from '@bibliocommons/utils-routing';
import SortDropDown from 'app/components/shared/SortDropDown';
import Pagination from '@bibliocommons/pagination';
import { didNotFindOptionsShape, libraryShape, paginationShape, routerShape } from '@bibliocommons/bc-prop-types';
import { TOGGLZ_FEATURES } from '@bibliocommons/constants-feature-togglz';

import Context from '@bibliocommons/context';
import ViewSelector from '../ViewSelector';
import ActiveFilters from '../ActiveFilters';
import { PersonalizedPromotionsContainer } from '../PersonalizedPromotions';
import RecommendedFeaturedContentContainer from '../RecommendedFeaturedContent/RecommendedFeaturedContentContainer';
import FacetDrawerToggle from '../../deprecated/shared/Facets/FacetDrawerToggle';
import SearchResultItem from '../SearchResultItem';
import DidNotFindExpandablePanel from '../DidNotFindExpandablePanel';

import './SearchResults.scss';

class SearchResults extends React.PureComponent {
  constructor(props) {
    super(props);
    bindAll(this);
  }

  componentDidMount() {
    this.fetchFeaturedCards();
    this.fetchGroupSearchSummaries();
  }

  componentDidUpdate(prevProps) {
    if (this.props.results !== prevProps.results || this.props.currentUser !== prevProps.currentUser) {
      this.fetchFeaturedCards();
      this.fetchGroupSearchSummaries();
    }
  }

  getPrintUrl() {
    const { search } = this.props.router.location;
    return `/v2/print/search${search}`;
  }

  fetchFeaturedCards() {
    if (this.context.features.get(TOGGLZ_FEATURES.CARDS_IN_CATALOG_DEV)) {
      const { cardsActions, router, results, library, pagination } = this.props;

      if (library.get('cmsEnabled') && pagination.get('page') === 1) {
        const metadataIds = results.map(result => result.representative?.get('id')).filter(id => id);

        if (metadataIds.size && router.query) {
          cardsActions.searchCards({ ...router.query, metadataIds, limit: 3 });
        }
      }
    }
  }

  fetchGroupSearchSummaries() {
    if (this.shouldShowGroupingToggle()) {
      this.props.searchActions.getGroupSearchSummaries(
        this.props.results
          .map(result => result.manifestations)
          .flatten(true)
          .map(result => result.get('id'))
          .toArray()
      );
    }
  }

  shouldShowGroupingToggle() {
    return (
      this.props.currentUser && this.props.currentUser.get('showGroupingDebug') && this.props.currentView === 'grouped'
    );
  }

  renderFeatureInCatalog() {
    if (this.context.features.get(TOGGLZ_FEATURES.CARDS_IN_CATALOG_DEV)) {
      return null;
    }

    return this.props.library.get('featuredContentEnabled') && <RecommendedFeaturedContentContainer />;
  }

  renderPersonalizedPromotions(itemIndex) {
    const { catalogSearchOptions, pagination, results, featuredCards } = this.props;
    const currentPage = pagination.get('page');
    const currentItemPosition = itemIndex + 1;

    // Piggyback on the search grouping debug logic to also show the
    // content cards debug button. Basically when the search grouping
    // debug checkbox is checked, also show the debug button for cards.
    const debugEnabled = this.shouldShowGroupingToggle() && catalogSearchOptions.get('showGroupings');

    if (currentPage === 1 && featuredCards.size) {
      // Display the first card row after the second item,
      // or after the first item if there's only 1 result.
      if (results.size === 1 || currentItemPosition === 2) {
        return (
          <PersonalizedPromotionsContainer
            debugEnabled={debugEnabled}
            position={currentItemPosition}
            cards={Immutable.List.of(featuredCards.first())}
          />
        );
      }

      // Display the second card row between the 7th and 8th items
      if (featuredCards.size > 1 && results.size > 7 && currentItemPosition === 7) {
        return (
          <PersonalizedPromotionsContainer
            debugEnabled={debugEnabled}
            position={currentItemPosition}
            cards={featuredCards.rest()}
          />
        );
      }
    }

    return null;
  }

  renderResultItems() {
    const startIndex = (this.props.pagination.get('page') - 1) * this.props.pagination.get('limit') + 1;
    let key = 0;
    return this.props.results.map((result, index) => (
      <CSSTransition classNames="fade" timeout={{ enter: 500, exit: 500 }} key={key++}>
        <AboveTheFoldOnlyServerRender skip={index > 4} key={key++}>
          <SearchResultItem
            reciprocalRank={1 / (startIndex + index)}
            dataKey="search-result-item"
            currentView={this.props.currentView}
            order={index > this.props.featuredContentBlockPosition ? 2 : 0}
            showSeries={this.props.searchType === 'series'}
            key={result.representative?.get('id')}
            item={result.representative}
            availabilityDetail={this.props.availabilityDetail}
            availabilityDetailActions={this.props.availabilityDetailActions}
            catalogSearchOptions={this.props.catalogSearchOptions}
            manifestations={result.manifestations}
            showJacketCover={this.props.currentView !== 'small'}
            groupSearchFeedbackActions={this.props.groupSearchFeedbackActions}
            branches={this.props.branches}
            currentUser={this.props.currentUser}
            renderCards={() => this.renderPersonalizedPromotions(index)}
          />
        </AboveTheFoldOnlyServerRender>
      </CSSTransition>
    ));
  }

  renderGroupingToggle() {
    const { searchActions, catalogSearchOptions } = this.props;

    if (!this.shouldShowGroupingToggle()) {
      return null;
    }

    return (
      <div className="grouping-summary-debug hidden-xs">
        <Checkbox
          id="grouping-summary-debug"
          label="Debug Groupings"
          handleChange={searchActions.toggleShowGroupings}
          checked={catalogSearchOptions.get('showGroupings')}
          inline
          labelBefore
        />
      </div>
    );
  }

  renderActiveFilters() {
    const { activeFilters, locked, singleBranch, searchType } = this.props;
    if (!activeFilters.isEmpty()) {
      return (
        <div className="active-filters row">
          <div className="col-md-12">
            <ActiveFilters
              searchType={searchType}
              singleBranch={singleBranch}
              activeFilters={activeFilters}
              locked={locked}
            />
          </div>
        </div>
      );
    }

    return null;
  }

  renderDidNotFindExpandablePanel() {
    const { didNotFindOptions, chatWidgets, query } = this.props;

    return (
      didNotFindOptions.get('isEnabled') && (
        <DidNotFindExpandablePanel query={query} didNotFindOptions={didNotFindOptions} chatWidgets={chatWidgets} />
      )
    );
  }

  render() {
    const { activeFilters, currentView, isFetching, pagination, searchActions, sortBys } = this.props;

    return (
      <section className="cp-search-results">
        {this.renderActiveFilters()}
        <section className="result-controls btn-toolbar">
          <FacetDrawerToggle
            drawerActions={this.props.drawerActions}
            activeFilterCount={activeFilters
              .toList()
              .flatten(true)
              .count()}
          />
          <SortDropDown options={sortBys} />
          <ViewSelector currentView={currentView} searchActions={searchActions} />
          <PrintButton className="hidden-xs hidden-sm" url={this.getPrintUrl()} iconColor="#333" />
          <Pagination
            autoFocus
            compact
            isFetching={isFetching}
            textKey="Search.paginationInfo"
            pagination={pagination}
          />
        </section>

        <section className="results-list row">
          <div className="col-md-12 results-list-items">
            <ul className="results">
              <TransitionGroup component={null}>
                {this.renderResultItems()}
                {this.renderFeatureInCatalog()}
              </TransitionGroup>
            </ul>
          </div>
        </section>
        <AboveTheFoldOnlyServerRender skip>
          <section className="bottom-controls">
            <Pagination showFirstPage isFetching={isFetching} textKey="Search.paginationInfo" pagination={pagination} />
            {this.renderGroupingToggle()}
          </section>
        </AboveTheFoldOnlyServerRender>
        <section className="did-not-find-panel">{this.renderDidNotFindExpandablePanel()}</section>
      </section>
    );
  }
}

const propTypes = {
  branches: ImmutablePropTypes.list,
  catalogSearchOptions: PropTypes.object,
  currentUser: PropTypes.object,
  currentView: PropTypes.string.isRequired,
  drawerActions: PropTypes.objectOf(PropTypes.func),
  featuredContentBlockPosition: PropTypes.number,
  isFetching: PropTypes.bool,
  locked: PropTypes.bool,
  searchActions: PropTypes.objectOf(PropTypes.func),
  searchType: PropTypes.string,
  singleBranch: PropTypes.bool,
  sortBys: ImmutablePropTypes.list.isRequired,
  didNotFindOptions: didNotFindOptionsShape,
  chatWidgets: ImmutablePropTypes.mapOf(PropTypes.object),
  pagination: paginationShape,
  results: ImmutablePropTypes.list,
  availabilityDetail: ImmutablePropTypes.map,
  featuredCards: ImmutablePropTypes.list.isRequired,
  availabilityDetailActions: PropTypes.objectOf(PropTypes.func),
  groupSearchFeedbackActions: PropTypes.objectOf(PropTypes.func),
  cardsActions: PropTypes.objectOf(PropTypes.func).isRequired,
  activeFilters: ImmutablePropTypes.map,
  library: libraryShape.isRequired,
  query: PropTypes.string,
  router: routerShape.isRequired
};

SearchResults.contextType = Context;

SearchResults.propTypes = propTypes;

export default withRouter(SearchResults);
